import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiClient } from '../services/apiClient';
import { LogIn, Lock, User, TrendingUp, ShieldCheck, Search, BookOpen, Anchor } from 'lucide-react';
import { useTranslation } from 'react-i18next'; // IMPORT I18N

const LoginPage = () => {
  const { t } = useTranslation(); // INIT I18N
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const data = await apiClient.login(username, password);
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('username', username);
      navigate('/dashboard');
      window.location.reload(); 
    } catch (err) {
      setError(t('login.error_msg')); // TRADUCTION DE L'ERREUR
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row font-sans pt-14">
      
      {/* --- PARTIE GAUCHE : STRATÉGIE --- */}
      <div className="w-full md:w-1/2 bg-slate-900 text-white p-8 md:p-16 flex flex-col justify-center relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-600 rounded-full blur-[128px] opacity-20 -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-600 rounded-full blur-[128px] opacity-20 translate-y-1/2 -translate-x-1/2"></div>

        <div className="relative z-10 max-w-lg mx-auto md:mx-0">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-900/50 border border-indigo-700 text-indigo-300 text-xs font-bold uppercase tracking-wider mb-6">
                <BookOpen size={14}/> {t('login.badge_philo')}
            </div>
            
            <h1 className="text-3xl md:text-4xl font-black mb-6 leading-tight">
                {t('login.title_main')} <span className="text-indigo-400">{t('login.title_garp')}</span>
            </h1>
            
            <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                {t('login.subtitle')}
            </p>

            <div className="space-y-8">
                <div className="flex gap-4">
                    <div className="shrink-0 w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center text-indigo-400 border border-slate-700">
                        <ShieldCheck size={24}/>
                    </div>
                    <div>
                        <h3 className="font-bold text-xl text-white mb-1">{t('login.step1_title')}</h3>
                        <p className="text-sm text-slate-400 leading-relaxed">
                            {t('login.step1_desc')}
                        </p>
                    </div>
                </div>

                <div className="flex gap-4">
                    <div className="shrink-0 w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center text-green-400 border border-slate-700">
                        <TrendingUp size={24}/>
                    </div>
                    <div>
                        <h3 className="font-bold text-xl text-white mb-1">{t('login.step2_title')}</h3>
                        <p className="text-sm text-slate-400 leading-relaxed">
                            {t('login.step2_desc')}
                        </p>
                    </div>
                </div>

                <div className="flex gap-4">
                    <div className="shrink-0 w-12 h-12 bg-slate-800 rounded-xl flex items-center justify-center text-orange-400 border border-slate-700">
                        <Search size={24}/>
                    </div>
                    <div>
                        <h3 className="font-bold text-xl text-white mb-1">{t('login.step3_title')}</h3>
                        <p className="text-sm text-slate-400 leading-relaxed">
                            {t('login.step3_desc')}
                        </p>
                    </div>
                </div>
            </div>

            <div className="mt-10 pt-8 border-t border-slate-800 flex items-center gap-3 text-slate-500 text-sm font-medium">
                <Anchor size={16}/>
                <span>{t('login.horizon')}</span>
            </div>
        </div>
      </div>

      {/* --- PARTIE DROITE : LOGIN --- */}
      <div className="w-full md:w-1/2 bg-slate-50 flex items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-md bg-white p-10 rounded-3xl shadow-xl border border-slate-100">
            <div className="text-center mb-10">
               <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center text-white font-black text-2xl mx-auto mb-6 shadow-lg shadow-indigo-200">
                  <LogIn size={32} />
               </div>
               <h2 className="text-3xl font-black text-slate-900 mb-2">{t('login.welcome_back')}</h2>
               <p className="text-slate-500 font-medium">{t('login.login_desc')}</p>
            </div>

            {error && (
                <div className="bg-rose-50 border border-rose-100 text-rose-700 p-4 rounded-xl text-sm font-bold text-center mb-6 animate-pulse">
                    {error}
                </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-xs font-black text-slate-400 mb-2 ml-1 uppercase tracking-wider">{t('login.email_label')}</label>
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                        <User size={20}/>
                    </div>
                    <input 
                        type="email" 
                        className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all placeholder-slate-400" 
                        placeholder={t('login.email_placeholder')}
                        value={username} 
                        onChange={(e) => setUsername(e.target.value)} 
                        required 
                    />
                </div>
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-2 ml-1">
                    <label className="block text-xs font-black text-slate-400 uppercase tracking-wider">{t('login.password_label')}</label>
                </div>
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                        <Lock size={20}/>
                    </div>
                    <input 
                        type="password" 
                        className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all placeholder-slate-400" 
                        placeholder={t('login.password_placeholder')}
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)} 
                        required 
                    />
                </div>
              </div>

              <button 
                type="submit" 
                disabled={loading}
                className="w-full bg-indigo-600 text-white p-4 rounded-xl font-black text-lg hover:bg-indigo-700 active:scale-[0.98] transition-all shadow-lg shadow-indigo-200 mt-4 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? t('login.btn_loading') : t('login.btn_login')}
              </button>
            </form>

            <div className="mt-8 text-center pt-6 border-t border-slate-100">
              <p className="text-slate-500 font-medium text-sm">
                  {t('login.no_account')} <Link to="/register" className="text-indigo-600 font-black hover:text-indigo-800 transition-colors ml-1">{t('login.btn_register')}</Link>
              </p>
            </div>
        </div>
      </div>
    </div>
  );
};
export default LoginPage;
